import { useCallback, useEffect, useState } from 'react'
import { useEnv } from '@praxis/component-runtime-env'
import {
  Button,
  Grid,
  Marker,
  Popover,
  Spinner,
  Timeline,
} from '@design-systems/canvas-ui'
import { IconButton } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserCheck } from '@fortawesome/free-solid-svg-icons'
import { MessageRounded } from '@mui/icons-material'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutline'
import { CustomTimelineItem } from '../../common/CustomTimelineItem'
import { getRFIAuditTrails } from '../../services/lcmService'
import { ErrorDialog, CustomTooltip, getColor } from 'mvscommoncomponents'
import {
  CODE_RFI_TYPE_QUESTIONNAIRE,
  LABEL_RFI3_APPROVED,
  MANUFACTURING_TYPE_ID_TRIM,
  SERVICE_NAME,
  STATUS_APPROVED,
  STATUS_RFI3_APPROVED,
} from '../../common/commonConstants'
import Icon, { ListCheckIcon } from '@design-systems/react-icons'

const RFIAuditTrail = (props) => {
  const env = useEnv()
  const [rfiAuditTrailData, setRfiAuditTrailData] = useState([])
  const [isRFIAuditFetchingInProgress, setIsRFIAuditFetchingInProgress] =
    useState(true)
  const [isRFIAuditFailed, setIsRFIAuditFailed] = useState(false)
  const [targetShowMore, setTargetShowMore] = useState(false)
  const [vendorShowMore, setVendorShowMore] = useState(false)

  const statusColorMapper = env.mfelcm?.appConstants?.statusColorMapper

  const getRFIAudits = useCallback(async () => {
    try {
      setIsRFIAuditFetchingInProgress(true)
      setIsRFIAuditFailed(false)
      setRfiAuditTrailData([])
      let response = await getRFIAuditTrails(
        props.rfiType,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )
      if (response) {
        setIsRFIAuditFetchingInProgress(false)
        setIsRFIAuditFailed(false)
        setRfiAuditTrailData(response)
        if (props.setAuditData) {
          props.setAuditData(response)
        }
        if (props.getLastRFI3Activity) {
          emitLastRFI3Activity(response)
        }
      } else {
        setIsRFIAuditFetchingInProgress(false)
        setIsRFIAuditFailed(false)
        setRfiAuditTrailData([])
      }
    } catch (error) {
      setIsRFIAuditFetchingInProgress(false)
      setIsRFIAuditFailed(true)
      setRfiAuditTrailData([])
    }
  }, [props.rfiType, props.supplierId, props.locationId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.isReadonly && props.supplierId) {
      getRFIAudits()
    }
  }, [props.supplierId, getRFIAudits, props.isReadonly])

  useEffect(() => {
    if (props.isReadonly && props.supplierId && props.refreshRFIScreen) {
      getRFIAudits()
    }
  }, [props.supplierId, props.refreshRFIScreen, getRFIAudits, props.isReadonly])

  /**
   * Emit last RFI3 Activity
   * @param {*} auditTrailList
   */
  const emitLastRFI3Activity = (auditTrailList) => {
    let arr = auditTrailList.filter(
      (eachAudit) => eachAudit.rfi_string === CODE_RFI_TYPE_QUESTIONNAIRE,
    )
    if (arr?.length > 0) {
      props.getLastRFI3Activity(arr[arr.length - 1].vetting_status)
    }
  }

  const getEmailToolTip = (rfiAudit) => {
    return (
      <Popover
        aria-label="Notified team members"
        content={
          <div className="hc8-pa-2x">
            {rfiAudit.target_email_ids.length > 0 && (
              <>
                <div className="hc8-mb-2x">
                  <u>
                    <strong>Target Team Members Notified</strong>
                  </u>
                </div>
                <div className="hc8-mb-1x">{rfiAudit.target_email_ids[0]}</div>
                {targetShowMore &&
                  rfiAudit.target_email_ids.slice(1).map((email, index) => (
                    <div
                      className="hc8-mb-1x"
                      key={`target-email-${index + 1}`}
                    >
                      {email}
                    </div>
                  ))}
                {rfiAudit.target_email_ids.length > 1 && (
                  <Marker
                    className="hc8-mt-2x"
                    size="sm"
                    onClick={() => setTargetShowMore(!targetShowMore)}
                  >
                    {targetShowMore ? 'show less' : 'show more'}
                  </Marker>
                )}
              </>
            )}
            {rfiAudit.non_target_email_ids.length > 0 && (
              <div className="hc8-pt-3x">
                <div className="hc8-mb-2x">
                  <u>
                    <strong>Vendor Contacts Notified</strong>
                  </u>
                </div>
                <div className="hc8-mb-1x">
                  {rfiAudit.non_target_email_ids[0]}
                </div>
                {vendorShowMore &&
                  rfiAudit.non_target_email_ids.slice(1).map((email, index) => (
                    <div
                      className="hc8-mb-1x"
                      key={`vendor-email-${index + 1}`}
                    >
                      {email}
                    </div>
                  ))}
                {rfiAudit.non_target_email_ids.length > 1 && (
                  <Marker
                    className="hc8-mt-2x"
                    size="sm"
                    onClick={() => setVendorShowMore(!vendorShowMore)}
                  >
                    {vendorShowMore ? 'show less' : 'show more'}
                  </Marker>
                )}
              </div>
            )}
          </div>
        }
        location="left"
        variant="clickOnly"
      >
        <Button
          aria-label="Show notified team members"
          className="hc8-ml-2x"
          isIconOnly
          variant="ghost"
        >
          <FontAwesomeIcon icon={faUserCheck} />
        </Button>
      </Popover>
    )
  }

  return (
    <Grid.Container>
      {!isRFIAuditFetchingInProgress && !isRFIAuditFailed && (
        <Grid.Item
          className="hc8-ma-4x hc8-pa-2x hc8-clr-placeholder border-all-grey-thin border-radius"
          size={{ xs: 12 }}
        >
          Audit trail gives step by step history of a document’s changes
        </Grid.Item>
      )}
      <Grid.Container className="hc8-pa-3x hc8-mt-none" id="rfi-approval-audit">
        {isRFIAuditFetchingInProgress && !isRFIAuditFailed && (
          <Grid.Item
            size={{ xs: 12 }}
            className="align-center-center hc8-bg-base hc8-mt-3x"
          >
            <Spinner size="lg" />
          </Grid.Item>
        )}

        {!isRFIAuditFetchingInProgress && isRFIAuditFailed && (
          <Grid.Item
            size={{ xs: 12 }}
            className="align-center-center hc8-bg-base hc8-mt-3x"
          >
            <ErrorOutlineRoundedIcon
              className="error-color"
              onClick={() => getRFIAudits()}
            />
          </Grid.Item>
        )}

        {!isRFIAuditFetchingInProgress &&
          !isRFIAuditFailed &&
          (rfiAuditTrailData.length === 0 ? (
            <Grid.Item size={{ xs: 12 }}>
              <ErrorDialog />
            </Grid.Item>
          ) : (
            <Timeline
              className="hc8-pt-none hc8-pl-3x hc8-pr-3x hc8-pb-2x hc8-mt-none"
              data-testid="audit-trail-timeline"
            >
              {rfiAuditTrailData.map((rfiAudit, auditIndex) =>
                rfiAudit.user_type === 'internal' ? (
                  <CustomTimelineItem
                    id={rfiAudit.user_type}
                    vetting_status={rfiAudit.vetting_status}
                    component={
                      <div>
                        <p>
                          <strong>
                            {rfiAudit.vetting_status.toUpperCase() ===
                            STATUS_RFI3_APPROVED
                              ? LABEL_RFI3_APPROVED
                              : rfiAudit.vetting_status
                                  ?.charAt(0)
                                  ?.toUpperCase() +
                                rfiAudit.vetting_status?.slice(1).toLowerCase()}
                          </strong>
                        </p>
                        <p className="hc8-clr-placeholder">
                          {rfiAudit.username}{' '}
                          <span
                            style={{
                              color: getColor(
                                rfiAudit.vetting_status,
                                statusColorMapper,
                              ),
                            }}
                          >
                            {rfiAudit.vetting_status.toUpperCase() ===
                            STATUS_RFI3_APPROVED
                              ? STATUS_APPROVED
                              : rfiAudit.vetting_status.toLowerCase()}
                          </span>{' '}
                          the{' '}
                          {rfiAudit.rfi_string.toUpperCase() === 'RFI1'
                            ? 'Business Partner Questionnaire'
                            : 'Manufacturing Details Questionnaire'}{' '}
                          ( {rfiAudit.rfi_string.toUpperCase()} )
                          {rfiAudit.vetting_comment && (
                            <CustomTooltip
                              key={`tooltip-notes-by-user`}
                              placement="right"
                              title={
                                <div className="hc8-pa-2x">
                                  <u>
                                    <strong>
                                      Notes by {rfiAudit.username}
                                    </strong>
                                  </u>
                                  <br />
                                  {rfiAudit.vetting_comment}
                                </div>
                              }
                              arrow
                              component={
                                <IconButton size="small">
                                  <MessageRounded className="vertical-align-sub"></MessageRounded>
                                </IconButton>
                              }
                            />
                          )}
                          {(rfiAudit.target_email_ids.length > 0 ||
                            rfiAudit.non_target_email_ids > 0) &&
                            getEmailToolTip(rfiAudit)}
                          {rfiAudit.capability_status?.length > 0 && (
                            <CustomTooltip
                              key={`approved-capability-status`}
                              placement="left"
                              title={
                                <div className="hc8-pa-2x">
                                  <u>
                                    <strong>Approved Capabilities</strong>
                                  </u>
                                  <br />
                                  {rfiAudit.capability_status
                                    ?.filter((c) => c.vetting_status?.length)
                                    .map((capability) =>
                                      props.manufacturingTypeId ===
                                        MANUFACTURING_TYPE_ID_TRIM &&
                                      capability.type !== SERVICE_NAME ? (
                                        <div>
                                          {capability.primary_type} -{' '}
                                          {capability.sub_type}
                                        </div>
                                      ) : (
                                        <div>{capability.primary_type}</div>
                                      ),
                                    )}
                                  <br />
                                  <u>
                                    <strong>Comments</strong>
                                  </u>
                                  <div>
                                    {rfiAudit.capability_vetting_message}
                                  </div>
                                </div>
                              }
                              arrow
                              component={
                                <Icon
                                  className="hc8-ml-2x hc8-mb-1x"
                                  style={{ marginBottom: '-5px' }}
                                  svg={ListCheckIcon}
                                  aria-hidden
                                />
                              }
                            />
                          )}
                        </p>
                        {rfiAudit.ctv_sourcing_information &&
                          rfiAudit.ctv_sourcing_information?.ctv_contacts
                            .length > 0 && (
                            <p className="hc8-clr-placeholder">
                              {
                                rfiAudit.ctv_sourcing_information
                                  ?.last_updated_by
                              }{' '}
                              assigned{' '}
                              <strong>
                                {
                                  rfiAudit.ctv_sourcing_information
                                    ?.ctv_contacts[0]?.sourcing_office
                                }
                              </strong>{' '}
                              as the CTV Sourcing Office and{' '}
                              <strong>
                                {rfiAudit.ctv_sourcing_information?.ctv_contacts[0]?.sourcing_contact?.map(
                                  (sc) =>
                                    sc?.first_name + ' ' + sc?.last_name + ', ',
                                )}
                              </strong>{' '}
                              as the CTV Sourcing Managers
                            </p>
                          )}
                        <p className="hc8-clr-disabled">
                          {rfiAudit.updated_at}
                        </p>
                      </div>
                    }
                  />
                ) : (
                  <CustomTimelineItem
                    id="external"
                    vetting_status={rfiAudit.vetting_status}
                    component={
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <div id="partnerText">
                          <p className="hc8-clr-placeholder">
                            Partner{' '}
                            {!rfiAudit.is_switched ? (
                              <>
                                <span
                                  style={{
                                    color: getColor(
                                      rfiAudit.vetting_status,
                                      statusColorMapper,
                                    ),
                                  }}
                                >
                                  {rfiAudit.vetting_status.toLowerCase()}
                                </span>{' '}
                                the{' '}
                                {rfiAudit.rfi_string.toUpperCase() === 'RFI1'
                                  ? 'Business Partner Questionnaire'
                                  : 'Manufacturing Details Questionnaire'}{' '}
                                ( {rfiAudit.rfi_string.toUpperCase()} )
                              </>
                            ) : (
                              <>
                                <span>
                                  added <b>{rfiAudit.switch_to}</b> business
                                </span>
                              </>
                            )}
                            {(rfiAudit.target_email_ids.length > 0 ||
                              rfiAudit.non_target_email_ids.length > 0) &&
                              getEmailToolTip(rfiAudit)}
                          </p>
                        </div>
                        <div className="hc8-clr-disabled hc8-pt-2x">
                          {rfiAudit.updated_at}
                        </div>
                      </div>
                    }
                  />
                ),
              )}
            </Timeline>
          ))}
      </Grid.Container>
    </Grid.Container>
  )
}

export default RFIAuditTrail
